import { z } from 'zod';
import { isValidCpfDv, isValidCnpjDv, stripDocumentoMask } from '../../shared/utils/document';

/**
 * Schema do `EmployeeInput`. Validações replicadas das rules do server
 * para evitar round-trip de 400 — `checkCPF.Udp` e `checkCNPJ.Udp` rodam upstream
 * também.
 *
 * Validações condicionais (PJ exige empresaNome/CNPJ) via `superRefine`.
 */
export const employeeInputSchema = z
  .object({
    nome: z.string().trim().min(1, 'Obrigatório').max(150, 'Máximo 150 caracteres'),
    cpf: z
      .string()
      .trim()
      .min(1, 'Obrigatório')
      .refine((v) => isValidCpfDv(v), 'CPF inválido'),
    sexo: z.enum(['F', 'M'], { required_error: 'Obrigatório' }),
    tipoContrato: z.enum(['CLT', 'PJ', 'TMP'], { required_error: 'Obrigatório' }),
    empresaNome: z.string().trim().max(150).nullable().optional(),
    empresaCnpj: z.string().trim().nullable().optional(),
    email: z
      .string()
      .trim()
      .min(1, 'Obrigatório')
      .email('E-mail inválido')
      .max(150, 'Máximo 150 caracteres'),
    telefone: z.string().trim().max(20).nullable().optional(),
    receberEmail: z.boolean().optional(),
    login: z
      .string()
      .trim()
      .max(60, 'Máximo 60 caracteres')
      .nullable()
      .optional()
      .transform((v) => (v == null || v === '' ? null : v)),
    tipoPagamento: z.enum(['H', 'F'], { required_error: 'Obrigatório' }),
    valorHoraPagar: z.string().default('0'),
    valorHoraReceber: z.string().default('0'),
    valorFixoPagar: z.string().default('0'),
    valorFixoReceber: z.string().default('0'),
    cargoId: z.number().int().positive().nullable().optional(),
    // Quirk server-side: endereco é obrigatório por Rule (apesar de nullable no schema).
    endereco: z.string().trim().min(1, 'Obrigatório').max(500, 'Máximo 500 caracteres'),
    dataAdmissao: z.string().trim().min(1, 'Obrigatório'),
    dataDemissao: z.string().trim().nullable().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.tipoContrato === 'PJ') {
      if (!data.empresaNome || data.empresaNome.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['empresaNome'],
          message: 'Obrigatório para PJ',
        });
      }
      if (!data.empresaCnpj || stripDocumentoMask(data.empresaCnpj).length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['empresaCnpj'],
          message: 'Obrigatório para PJ',
        });
      } else if (!isValidCnpjDv(data.empresaCnpj)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['empresaCnpj'],
          message: 'CNPJ inválido',
        });
      }
    }
  });

export type EmployeeFormValues = z.input<typeof employeeInputSchema>;

export const employeePasswordSchema = z.object({
  senha: z.string().min(8, 'Mínimo 8 caracteres').max(60, 'Máximo 60 caracteres'),
});

export type EmployeePasswordFormValues = z.infer<typeof employeePasswordSchema>;
